import { parseEuroToCents, type AdminProject, type AdminUser, type Lookups, type ProjectInput } from '@ft/shared';
import { useCallback, useState } from 'react';
import { useI18n } from '../../i18n';
import { centsToField, dfmt, eur } from '../../lib/format';
import { api } from '../../lib/http';
import { getAreaId, getAreaName } from '../../lib/area';
import { Cust, Loading, Modal, useLoad, useToast } from '../ui';

const NEW = '__new__';

export function ProjectsAdmin() {
  const { t } = useI18n();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const { data, error, reload } = useLoad<AdminProject[]>(`/api/admin/projects?year=${year}`);
  const { data: users } = useLoad<AdminUser[]>('/api/admin/users');
  const { data: lookups, reload: reloadLookups } = useLoad<Lookups>('/api/lookups');
  const [form, setForm] = useState<{ project: AdminProject | null } | null>(null);
  const [toast, say] = useToast();
  const name = (id: string | null) => users?.find((u) => u.id === id);
  const ready = !!users && !!lookups;

  return (
    <>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <p className="muted lead" style={{ flex: 1 }}>{t('projects.lead', { area: getAreaName() })}</p>
        <div className="fld">
          <label htmlFor="py">{t('projects.year')}</label>
          <select id="py" className="yearsel" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => <option key={y}>{y}</option>)}
          </select>
        </div>
        <button className="btn primary" style={{ alignSelf: 'flex-end' }} onClick={() => setForm({ project: null })} disabled={!ready}>{t('projects.add')}</button>
      </div>
      {!data ? <Loading error={error} /> : (
        <div className="box tablewrap">
          {!data.length ? <div className="empty">{t('projects.none')}</div> : (
            <table>
              <thead><tr><th>{t('common.project')}</th><th>{t('common.customer')}</th><th>{t('projects.col.domain')}</th><th>{t('common.pm')} <span className="req">*</span></th><th>{t('projects.col.deputy')}</th><th>{t('projects.col.since')}</th><th className="r">{t('projects.col.target', { y: year })}</th></tr></thead>
              <tbody>
                {data.map((p) => {
                  const pm = name(p.pmUserId);
                  const dep = name(p.deputyUserId);
                  return (
                    <tr key={p.id} className="click" tabIndex={0} onClick={() => ready && setForm({ project: p })} onKeyDown={(e) => { if (e.key === 'Enter' && ready) setForm({ project: p }); }}>
                      <td><b style={{ fontWeight: 600 }}>{p.name}</b></td>
                      <td><Cust name={p.customer.name} /></td>
                      <td className={p.domain ? undefined : 'muted'}>{p.domain ?? '–'}{p.orderFolder && <span className="tag" title={p.orderFolder}>{t('pf.folderTag')}</span>}</td>
                      <td>{pm ? <><span className="pmc">{pm.initials}</span> {pm.name}</> : <span className="pill crit">{t('common.withoutPm')}</span>}</td>
                      <td>{dep ? dep.name : <span className="muted">–</span>}</td>
                      <td className="num muted">{dfmt(p.pmSince)}</td>
                      <td className="r num">{p.targetCents !== null ? eur(p.targetCents) : '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      {form && users && lookups && (
        <ProjectForm project={form.project} year={year} users={users} lookups={lookups} domains={[...new Set(data?.map((p) => p.domain).filter((d): d is string => !!d))].sort()}
          onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); reloadLookups(); say(t('pf.saved')); }} />
      )}
      {toast}
    </>
  );
}

function ProjectForm({ project, year, users, lookups, domains, onClose, onSaved }: {
  project: AdminProject | null; year: number; users: AdminUser[]; lookups: Lookups; domains: string[]; onClose: () => void; onSaved: () => void;
}) {
  const { t, lang } = useI18n();
  const [customerId, setCustomerId] = useState(project?.customer.id ?? '');
  const [newCustomer, setNewCustomer] = useState('');
  const [name, setName] = useState(project?.name ?? '');
  const [domain, setDomain] = useState(project?.domain ?? '');
  const [folder, setFolder] = useState(project?.orderFolder ?? '');
  const [wd, setWd] = useState(project?.workdayProjectId ?? '');
  const [fcFile, setFcFile] = useState(project?.forecastFileUrl ?? '');
  const [pm, setPm] = useState(project?.pmUserId ?? '');
  const [deputy, setDeputy] = useState(project?.deputyUserId ?? '');
  const [target, setTarget] = useState(project?.targetCents != null ? centsToField(project.targetCents) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = useCallback(() => { if (!busy) onClose(); }, [busy, onClose]);
  const people = users.filter((u) => u.active && u.role !== 'reader' && u.role !== 'finance' && u.areaIds.includes(getAreaId() ?? ''));
  const targetCents = target.trim() ? parseEuroToCents(target, lang) : null;

  async function save() {
    setError(null);
    if (!project && !customerId) return setError(t('pf.err.customer'));
    if (!project && customerId === NEW && !newCustomer.trim()) return setError(t('pf.err.customer'));
    if (!name.trim()) return setError(t('pf.err.name'));
    if (!project && !pm) return setError(t('pf.err.pm'));
    if (target.trim() && (targetCents === null || targetCents < 0)) return setError(t('pf.err.target'));
    if (wd.trim() && !/^PROJECT-\d{1,8}$/i.test(wd.trim())) return setError(t('pf.err.workday'));
    if (fcFile.trim() && !/^https:\/\/\S+$/i.test(fcFile.trim())) return setError(t('pf.err.fcFile'));
    const body: ProjectInput = {
      version: project?.version,
      customerId: project ? undefined : customerId === NEW ? null : customerId,
      newCustomerName: !project && customerId === NEW ? newCustomer.trim() : null,
      name: name.trim(), domain: domain.trim() || null, orderFolder: folder.trim().replace(/^"|"$/g, '') || null, workdayProjectId: wd.trim().toUpperCase() || null, forecastFileUrl: fcFile.trim() || null, pmUserId: pm || null, deputyUserId: deputy || null,
      year, targetCents,
    };
    setBusy(true);
    try {
      if (project) await api('PUT', `/api/admin/projects/${project.id}`, body);
      else await api('POST', '/api/admin/projects', body);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal title={project ? t('pf.edit', { n: project.name }) : t('pf.new')} crumb={t('admin.title')} onClose={close} width={640}
      footer={<>
        {error && <p className="err" role="alert">{error}</p>}
        <button className="btn" onClick={close} disabled={busy}>{t('common.cancel')}</button>
        <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('common.saving') : t('common.save')}</button>
      </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <div className="fld">
          <label htmlFor="pf-c">{t('pf.customer')} <span className="req">*</span></label>
          {project ? <input id="pf-c" value={project.customer.name} disabled /> : (
            <select id="pf-c" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">{t('common.choose')}</option>
              {lookups.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              <option value={NEW}>{t('pf.newCustomer')}</option>
            </select>
          )}
        </div>
        {customerId === NEW && !project ? (
          <div className="fld">
            <label htmlFor="pf-nc">{t('pf.newCustomerName')} <span className="req">*</span></label>
            <input id="pf-nc" value={newCustomer} onChange={(e) => setNewCustomer(e.target.value)} autoComplete="off" />
          </div>
        ) : <div />}
        <div className="fld">
          <label htmlFor="pf-n">{t('pf.name')} <span className="req">*</span></label>
          <input id="pf-n" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </div>
        <div className="fld">
          <label htmlFor="pf-d">{t('pf.domain')}</label>
          <input id="pf-d" list="pf-domains" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder={t('pf.domainPh')} autoComplete="off" />
          <datalist id="pf-domains">{domains.map((d) => <option key={d} value={d} />)}</datalist>
        </div>
        <div className="fld full">
          <label htmlFor="pf-f">{t('pf.folder')}</label>
          <input id="pf-f" className="mono" value={folder} onChange={(e) => setFolder(e.target.value)} placeholder={t('pf.folderPh')} autoComplete="off" />
          <span className="help">{t('pf.folderHelp')}</span>
        </div>
        <div className="fld">
          <label htmlFor="pf-wd">{t('pf.workday')}</label>
          <input id="pf-wd" className="mono" value={wd} onChange={(e) => setWd(e.target.value)} placeholder="PROJECT-13113" autoComplete="off" />
          <span className="help">{t('pf.workdayHelp')}</span>
        </div>
        <div />
        <div className="fld full">
          <label htmlFor="pf-fc">{t('pf.fcFile')}</label>
          <input id="pf-fc" className="mono" value={fcFile} onChange={(e) => setFcFile(e.target.value)} placeholder={project?.forecastFileAuto ? t('pf.fcFileAuto', { n: project.forecastFileAuto.name ?? project.forecastFileAuto.url }) : 'https://…sharepoint.com/…'} autoComplete="off" />
          <span className="help">{t('pf.fcFileHelp')}</span>
        </div>
        <div className="fld">
          <label htmlFor="pf-pm">{t('pf.pm')}{!project && <span className="req"> *</span>}</label>
          <select id="pf-pm" value={pm} onChange={(e) => { setPm(e.target.value); if (e.target.value === deputy) setDeputy(''); }}>
            <option value="">{t('pf.noPm')}</option>
            {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="fld">
          <label htmlFor="pf-dep">{t('pf.deputy')}</label>
          <select id="pf-dep" value={deputy} onChange={(e) => setDeputy(e.target.value)}>
            <option value="">{t('pf.noDeputy')}</option>
            {people.filter((u) => u.id !== pm).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <p className="help full" style={{ margin: 0 }}>{t('pf.pmHelp', { area: getAreaName() })}</p>
        <div className="fld">
          <label htmlFor="pf-t">{t('pf.target', { y: year })}</label>
          <input id="pf-t" className={`r${target.trim() && targetCents === null ? ' bad' : ''}`} inputMode="decimal" value={target} placeholder={t('pf.targetPh')}
            onChange={(e) => setTarget(e.target.value)} onBlur={() => { if (targetCents !== null) setTarget(centsToField(targetCents)); }} />
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
